/**
 * FikoUretimKarti Dialog — Üretim Kartı / Maliyet Dashboard (capstone)
 * Modeli tek tıkla uçtan uca analiz: geometri + tanınan özellikler + maliyet kırılımı → üretim kartı.
 * Mevcut motorları birleştirir: FikoMassInfo, ozellikleriTani, teklifHesapla, kaplamaHesapla.
 */
import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { FikoMassInfo, MALZEMELER } from '../fiko-mass-info/FikoMassInfo';
import { MALZEME_PIYASA, teklifHesapla, TeklifSonuc } from '../fiko-teklif/FikoTeklif';
import { ozellikleriTani, ozellikOzet } from '../fiko-feature-tani/FikoFeatureTani';
import { KAPLAMA_TIPLERI, kaplamaHesapla } from '../fiko-kaplama/FikoKaplama';
import { baglamYaz } from '../fiko-uretim-baglam/UretimBaglam';
import { KalicilikService } from '../fiko-kalicilik/kalicilik.service';

@Component({
  selector: 'fikoyt-uretim-karti',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="uk">
    <div class="uk-ust">
      <select [(ngModel)]="malzemeIdx" (ngModelChange)="malzemeDegisti()">
        @for (m of malzemeler; track $index) { <option [ngValue]="$index">{{ m.ad }}</option> }
      </select>
      <input type="number" [(ngModel)]="adet" (ngModelChange)="hesapla()" min="1" title="Adet" class="uk-adet">
    </div>
    <div class="uk-opsiyon">
      <label class="uk-chk"><input type="checkbox" [(ngModel)]="kaplamaAktif" (ngModelChange)="hesapla()"> Kaplama</label>
      @if (kaplamaAktif) {
        <select [(ngModel)]="kaplamaIdx" (ngModelChange)="hesapla()" class="uk-ksel">
          @for (k of kaplamalar; track $index) { <option [ngValue]="$index">{{ k.ad }}</option> }
        </select>
      }
      <label class="uk-chk2">Kâr% <input type="number" [(ngModel)]="kar" (ngModelChange)="hesapla()" min="0" class="uk-mini-in"></label>
    </div>
    <button class="uk-btn primary" (click)="analiz()">⚙️ Modeli Analiz Et</button>

    @if (analizVar) {
      <div class="uk-kart">
        <div class="uk-bolum">📐 Geometri</div>
        <div class="uk-r"><span>Ağırlık (net/brüt)</span><b>{{ f(netKg,2) }} / {{ f(brutKg,2) }} kg</b></div>
        <div class="uk-r"><span>Hacim · Yüzey</span><b>{{ f(hacimCm3,0) }} cm³ · {{ f(alanDm2,1) }} dm²</b></div>
        <div class="uk-r"><span>Ölçü (mm)</span><b>{{ olcu }}</b></div>

        <div class="uk-bolum">🔍 Özellikler</div>
        <div class="uk-cips">
          @if (ozet.delik) { <span class="uk-cip">⊙ {{ ozet.delik }} delik</span> }
          @if (ozet.cep) { <span class="uk-cip">▭ {{ ozet.cep }} cep</span> }
          @if (ozet.duzlemUst) { <span class="uk-cip">▱ {{ ozet.duzlemUst }} düzlem</span> }
          @if (ozet.pah) { <span class="uk-cip">◣ {{ ozet.pah }} pah</span> }
          @if (!ozet.delik && !ozet.cep && !ozet.pah) { <span class="uk-cip bos">özellik yok</span> }
        </div>

        <div class="uk-bolum">💰 Maliyet Kırılımı</div>
        <div class="uk-r"><span>Hammadde</span><b>{{ f(teklif.hammaddeMaliyet) }} ₺</b></div>
        <div class="uk-r"><span>İşleme ({{ f(teklif.islemeSureDk,0) }} dk)</span><b>{{ f(teklif.islemeMaliyet) }} ₺</b></div>
        <div class="uk-r"><span>Kurulum (birim)</span><b>{{ f(teklif.kurulumBirim) }} ₺</b></div>
        @if (kaplamaAktif) { <div class="uk-r"><span>Kaplama ({{ kaplamalar[kaplamaIdx].ad }})</span><b>{{ f(kaplamaMaliyet) }} ₺</b></div> }
        <div class="uk-r alt"><span>Birim maliyet</span><b>{{ f(teklif.birimMaliyet) }} ₺</b></div>
        <div class="uk-r"><span>Kâr (%{{ kar }})</span><b>{{ f(teklif.birimKar) }} ₺</b></div>
        <div class="uk-r vurgu"><span>BİRİM FİYAT (KDV dahil)</span><b>{{ f(teklif.birimKdvli) }} ₺</b></div>
        <div class="uk-r toplam"><span>TOPLAM ({{ adet }} ad)</span><b>{{ f(teklif.toplamKdvli) }} ₺</b></div>
      </div>
      <div class="uk-kayit-row">
        <input [(ngModel)]="parcaAd" placeholder="Parça adı/no" class="uk-parca">
        <button class="uk-btn kaydet" (click)="kaydet()">💾 Kaydet</button>
      </div>
      @if (kayitDurum) { <div class="uk-kayit-durum">{{ kayitDurum }}</div> }
      <button class="uk-btn" (click)="yazdir()">🖨️ Üretim Kartı Yazdır</button>
      <div class="uk-ilgili">
        <div class="uk-ilgili-b">İlgili işlemler →</div>
        <div class="uk-ilgili-g">
          <button (click)="ilgiliAc('acTeklifDialog')">🧮 Teklif</button>
          <button (click)="ilgiliAc('acIsEmriDialog')">▣ İş Emri</button>
          <button (click)="ilgiliAc('acMuayeneDialog')">✓ Muayene</button>
          <button (click)="ilgiliAc('acKaplamaDialog')">🎨 Kaplama</button>
          <button (click)="ilgiliAc('acDxfDialog')">📄 DXF</button>
          <button (click)="ilgiliAc('acKayitlarDialog')">🗂️ Kayıtlar</button>
        </div>
      </div>
    }
    @if (hata) { <div class="uk-hata">{{ hata }}</div> }
  </div>
  `,
  styles: [`
    .uk { padding:10px; min-width:310px; max-width:350px; font-size:11px; color:#e6f6ff;
      background:linear-gradient(180deg,rgba(0,40,60,.96),rgba(0,18,36,.98)); border-radius:6px; }
    .uk-ust { display:flex; gap:6px; margin-bottom:6px; }
    .uk-ust select { flex:1; background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.4); color:#fff; padding:5px; border-radius:4px; }
    .uk-adet { width:56px; background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.4); color:#fff; padding:5px; border-radius:4px; outline:none; }
    .uk-opsiyon { display:flex; gap:8px; align-items:center; margin-bottom:6px; flex-wrap:wrap; }
    .uk-chk, .uk-chk2 { font-size:10px; color:#9fe8f7; display:flex; align-items:center; gap:4px; }
    .uk-ksel { background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.4); color:#fff; padding:3px; border-radius:4px; font-size:10px; }
    .uk-mini-in { width:44px; background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.35); color:#fff; padding:3px; border-radius:4px; outline:none; }
    .uk-btn { width:100%; padding:8px; margin-top:4px; border:none; border-radius:5px; font-weight:600; cursor:pointer; color:#fff; background:rgba(0,70,90,.8); }
    .uk-btn.primary { background:linear-gradient(90deg,#0a84c0,#0bb39e); }
    .uk-kayit-row { display:flex; gap:5px; margin-top:6px; }
    .uk-parca { flex:1; background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.35); color:#fff; padding:5px; border-radius:4px; outline:none; min-width:0; }
    .uk-btn.kaydet { width:auto; padding:6px 12px; margin:0; background:linear-gradient(90deg,#1f9e5a,#34c77b); }
    .uk-kayit-durum { font-size:10px; color:#7dffc0; text-align:center; margin-top:3px; }
    .uk-kart { margin-top:10px; background:rgba(0,0,0,.35); border:1px solid rgba(0,200,220,.3); border-radius:6px; padding:8px; }
    .uk-bolum { font-size:10px; font-weight:700; color:#5fd9f5; margin:8px 0 4px; border-bottom:1px solid rgba(0,200,220,.2); padding-bottom:2px; }
    .uk-bolum:first-child { margin-top:0; }
    .uk-r { display:flex; justify-content:space-between; padding:2px 0; } .uk-r span { color:#9fe8f7; }
    .uk-r.alt { margin-top:3px; border-top:1px dashed rgba(0,200,220,.3); padding-top:4px; }
    .uk-r.vurgu { background:rgba(0,200,120,.16); margin:4px -8px; padding:6px 8px; } .uk-r.vurgu b { color:#7dffc0; font-size:13px; }
    .uk-r.toplam { background:rgba(255,180,0,.14); margin:2px -8px -2px; padding:6px 8px; } .uk-r.toplam b { color:#ffd76a; font-size:14px; }
    .uk-cips { display:flex; flex-wrap:wrap; gap:4px; }
    .uk-cip { font-size:10px; padding:2px 7px; background:rgba(0,120,150,.3); border:1px solid rgba(0,200,220,.3); border-radius:10px; }
    .uk-cip.bos { background:rgba(120,120,120,.2); color:#aaa; }
    .uk-hata { margin-top:8px; padding:6px; background:rgba(255,50,50,.2); border:1px solid rgba(255,100,100,.4); border-radius:4px; color:#ff9a9a; text-align:center; }
    .uk-ilgili { margin-top:8px; border-top:1px dashed rgba(0,200,220,.3); padding-top:6px; }
    .uk-ilgili-b { font-size:9px; color:#5fd9f5; margin-bottom:4px; }
    .uk-ilgili-g { display:flex; flex-wrap:wrap; gap:4px; }
    .uk-ilgili-g button { flex:1; min-width:60px; padding:5px 4px; font-size:10px; border:1px solid rgba(0,200,220,.3); background:rgba(0,70,90,.6); color:#cfeaf5; border-radius:5px; cursor:pointer; }
    .uk-ilgili-g button:hover { background:rgba(0,120,150,.7); border-color:rgba(0,220,240,.5); }
  `]
})
export class FikoUretimKartiComponent {
  @Input() anaGosterim: any = null;
  @Input() pencere: any;
  @Input() ayarlar: any = {};
  @Input() modelleme: any = null;
  @Input() ana: any = null;   // parent 3D component (çapraz-link için)

  malzemeler = MALZEMELER;
  malzemeIdx = 1;
  kaplamalar = KAPLAMA_TIPLERI;
  kaplamaIdx = 0; kaplamaAktif = false;
  adet = 1; kar = 35;

  analizVar = false; hata: string | null = null;
  netKg = 0; brutKg = 0; talasCm3 = 0; hacimCm3 = 0; alanDm2 = 0; olcu = '';
  ozet: any = { delik: 0, cep: 0, duzlemUst: 0, pah: 0, silindirDis: 0 };
  kaplamaMaliyet = 0;
  teklif: TeklifSonuc = teklifHesapla({ netAgirlikKg: 0, brutAgirlikKg: 0, talasHacimCm3: 0, malzemeFiyatKg: 0, islemeHizCm3Dk: 1, saatUcreti: 0, kurulumDk: 0, kaplamaUcret: 0, ekIslemUcret: 0, karYuzde: 0, kdvYuzde: 20, adet: 1, fireYuzde: 0 });
  private mass = new FikoMassInfo();

  private kalicilik = inject(KalicilikService);
  parcaAd = ''; kayitDurum = '';

  constructor(private cd: ChangeDetectorRef) {
    const yuk = this.kalicilik.yuklenecekAl('uretim_karti');   // Kayıtlar'dan yükle → snapshot göster
    if (yuk) { Object.assign(this, yuk); this.analizVar = true; }
  }

  /** Üretim kartını DB + local'e kaydet (analiz snapshot'ı). */
  async kaydet(): Promise<void> {
    if (!this.analizVar) { this.kayitDurum = 'Önce analiz et'; this.cd.detectChanges(); return; }
    const veri = { parcaAd: this.parcaAd, malzemeIdx: this.malzemeIdx, kaplamaIdx: this.kaplamaIdx, kaplamaAktif: this.kaplamaAktif, adet: this.adet, kar: this.kar, netKg: this.netKg, brutKg: this.brutKg, talasCm3: this.talasCm3, hacimCm3: this.hacimCm3, alanDm2: this.alanDm2, olcu: this.olcu, ozet: this.ozet, kaplamaMaliyet: this.kaplamaMaliyet, teklif: this.teklif };
    const baslik = (this.parcaAd || this.malzemeler[this.malzemeIdx].ad + ' ' + this.olcu) + ' · ' + this.f(this.teklif.toplamKdvli) + '₺';
    this.kayitDurum = 'Kaydediliyor…'; this.cd.detectChanges();
    const k = await this.kalicilik.kaydet('uretim_karti', baslik, veri);
    this.kayitDurum = k._sync ? '✓ Kaydedildi (DB)' : '✓ Kaydedildi (yerel)'; this.cd.detectChanges();
  }

  malzemeDegisti(): void { if (this.analizVar) this.hesapla(); }

  analiz(): void {
    this.hata = null;
    const sahne = this.anaGosterim?.sahneler?.[0];
    const mesh = this.modelleme?.govdeMesh || this.anaGosterim?.modelleme?.govdeMesh;
    if (!sahne) { this.hata = 'Sahne yok'; this.cd.detectChanges(); return; }
    const mlz = this.malzemeler[this.malzemeIdx];
    this.mass.malzemeAyarla(mlz);
    const r = this.mass.hesaplaModel(this.modelleme || this.anaGosterim?.modelleme, sahne);   // aktif gövde (yoksa sahne)
    if (!r || r.ucgenSayisi === 0) { this.hata = 'Önce bir model oluşturun'; this.analizVar = false; this.cd.detectChanges(); return; }
    this.netKg = r.agirlik.kg; this.hacimCm3 = r.hacim.cm3; this.alanDm2 = r.yuzeyAlani.cm2 / 100;
    const b = r.boundingBox.boyutlar; this.olcu = `${b.x.toFixed(0)}×${b.y.toFixed(0)}×${b.z.toFixed(0)}`;
    const bboxCm3 = (b.x * b.y * b.z) / 1000;
    this.brutKg = bboxCm3 / 1000 * mlz.yogunluk;
    this.talasCm3 = Math.max(0, bboxCm3 - r.hacim.cm3);
    let delikler: number[] = [];
    try { const oz = mesh ? ozellikleriTani(mesh) : []; this.ozet = ozellikOzet(oz); delikler = oz.filter(o => o.tip === 'delik' && o.cap).map(o => +o.cap!.toFixed(1)); } catch { }
    this.analizVar = true;
    // modüller arası bağlam: iş emri/muayene/teklif bunu okuyup ön-doldurur
    baglamYaz({ malzeme: mlz.ad, olcu: this.olcu, netKg: this.netKg, hacimCm3: this.hacimCm3, alanDm2: this.alanDm2, delikler });
    this.hesapla();
  }

  /** Çapraz-link: ilgili modülü aç (bağlam zaten yazıldı → ön-dolu gelir). */
  ilgiliAc(metod: string): void {
    try { this.ana?.[metod]?.(); } catch { }
  }

  hesapla(): void {
    if (!this.analizVar) return;
    const mlz = this.malzemeler[this.malzemeIdx];
    const piyasa = MALZEME_PIYASA[mlz.ad] || { fiyatKg: 100, mrr: 25 };
    this.kaplamaMaliyet = 0;
    if (this.kaplamaAktif) {
      const t = this.kaplamalar[this.kaplamaIdx];
      this.kaplamaMaliyet = kaplamaHesapla(t, this.alanDm2, this.netKg, 1).maliyet;
    }
    this.teklif = teklifHesapla({
      netAgirlikKg: this.netKg, brutAgirlikKg: this.brutKg, talasHacimCm3: this.talasCm3,
      malzemeFiyatKg: piyasa.fiyatKg, islemeHizCm3Dk: piyasa.mrr, saatUcreti: 750, kurulumDk: 45,
      kaplamaUcret: this.kaplamaMaliyet, ekIslemUcret: 0, karYuzde: this.kar, kdvYuzde: 20, adet: this.adet, fireYuzde: 15,
    });
    this.cd.detectChanges();
  }

  yazdir(): void {
    if (!this.analizVar) return;
    const s = this.teklif, mlz = this.malzemeler[this.malzemeIdx].ad, tarih = new Date().toLocaleDateString('tr-TR');
    const w = window.open('', '_blank', 'width=820,height=1000'); if (!w) { this.hata = 'Popup engeli'; this.cd.detectChanges(); return; }
    const sat = (a: string, b: string, vurgu = false) => `<tr${vurgu ? ' style="background:#eef7ff;font-weight:700"' : ''}><td>${a}</td><td style="text-align:right">${b}</td></tr>`;
    const ozetMetin = [this.ozet.delik ? `${this.ozet.delik} delik` : '', this.ozet.cep ? `${this.ozet.cep} cep` : '', this.ozet.duzlemUst ? `${this.ozet.duzlemUst} düzlem` : ''].filter(Boolean).join(' · ') || '—';
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Üretim Kartı</title>
    <style>body{font-family:Segoe UI,Arial,sans-serif;color:#13283a;padding:28px;max-width:680px;margin:auto}
    h1{color:#0a84c0;margin:0 0 4px} .sub{color:#789;margin-bottom:18px;font-size:13px}
    h3{color:#0a84c0;margin:18px 0 6px;border-bottom:2px solid #0a84c0;padding-bottom:3px}
    table{width:100%;border-collapse:collapse;font-size:14px} td{padding:7px 10px;border-bottom:1px solid #e3edf5}
    .top{background:#0a84c0} .top td{color:#fff;font-size:18px;font-weight:700}</style></head><body>
    <h1>ÜRETİM KARTI</h1><div class="sub">Tarih: ${tarih} · Malzeme: ${mlz} · Adet: ${this.adet}</div>
    <h3>Geometri & Özellikler</h3><table>
    ${sat('Ağırlık (net / brüt)', `${this.f(this.netKg, 2)} / ${this.f(this.brutKg, 2)} kg`)}
    ${sat('Hacim · Yüzey', `${this.f(this.hacimCm3, 0)} cm³ · ${this.f(this.alanDm2, 1)} dm²`)}
    ${sat('Ölçü', this.olcu + ' mm')}
    ${sat('Tanınan özellikler', ozetMetin)}</table>
    <h3>Maliyet Kırılımı</h3><table>
    ${sat('Hammadde', this.f(s.hammaddeMaliyet) + ' ₺')}
    ${sat(`İşleme (${this.f(s.islemeSureDk, 0)} dk)`, this.f(s.islemeMaliyet) + ' ₺')}
    ${sat('Kurulum (birim)', this.f(s.kurulumBirim) + ' ₺')}
    ${this.kaplamaAktif ? sat('Kaplama (' + this.kaplamalar[this.kaplamaIdx].ad + ')', this.f(this.kaplamaMaliyet) + ' ₺') : ''}
    ${sat('Birim maliyet', this.f(s.birimMaliyet) + ' ₺')}
    ${sat(`Kâr (%${this.kar})`, this.f(s.birimKar) + ' ₺', true)}
    ${sat('Birim fiyat (KDV dahil)', this.f(s.birimKdvli) + ' ₺')}
    <tr class="top"><td>TOPLAM (KDV dahil)</td><td style="text-align:right">${this.f(s.toplamKdvli)} ₺</td></tr></table>
    <div style="margin-top:20px;font-size:12px;color:#789">FikoYT 3D Sistem ile oluşturulmuştur. Tahmini maliyet modeline dayanır.</div>
    <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  }

  f(v: number, d = 2): string { return (v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: d }); }
}
